import React, { createContext, useContext } from 'react'
import { Alert, AlertColor } from '@mui/material'
import Snackbar from '@mui/material/Snackbar'

type SnackBarContextActions = {
  showSnackBar: (text: string, typeColor: AlertColor) => void
}

const SnackBarContext = createContext({} as SnackBarContextActions)

interface SnackBarContextProviderProps {
  children: React.ReactNode
}

const SnackBarProvider: React.FC<SnackBarContextProviderProps> = ({ children }) => {
  const [open, setOpen] = React.useState<boolean>(false)
  const [message, setMessage] = React.useState<string>('')
  const [typeColor, setTypeColor] = React.useState<AlertColor | null>(null)

  const showSnackBar = (text: string, color: AlertColor) => {
    setMessage(text)
    setTypeColor(color)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setTypeColor(typeColor)
  }

  return (
    <SnackBarContext.Provider
      value={{
        showSnackBar,
      }}
    >
      {typeColor && (
        <Snackbar
          open={open}
          autoHideDuration={4500}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={handleClose}
            severity={typeColor}
            variant='filled'
            elevation={6}
            sx={{ width: '100%' }}
            style={{ whiteSpace: 'pre' }}
          >
            {message}
          </Alert>
        </Snackbar>
      )}
      {children}
    </SnackBarContext.Provider>
  )
}

const useSnackBar = (): SnackBarContextActions => {
  const context = useContext(SnackBarContext)

  if (!context) {
    throw new Error('useSnackBar must be used within an SnackBarProvider')
  }

  return context
}

export { SnackBarProvider, useSnackBar }
